import { getToken, onMessage } from 'firebase/messaging';
import api from '../config/axios';
import { getFirebaseMessaging, getVapidKey, isFirebaseConfigured } from '../config/firebase';
import { hasAuthToken, isImpersonationSession } from '../utils/authStorage';
import { showChatBrowserNotification, shouldSuppressChatNotification, resolvePushNotificationTitle } from './chatBrowserNotification';

const SW_PATH = '/firebase-messaging-sw.js';
let cachedToken = null;
let foregroundListenerAttached = false;
let registerInFlight = null;

function isPushEnvironmentReady() {
  if (typeof window === 'undefined') {
    return false;
  }
  const { protocol, hostname } = window.location;
  return protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
}

function hasBasicPushApis() {
  return (
    'serviceWorker' in navigator &&
    'Notification' in window &&
    'PushManager' in window
  );
}

async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return null;
  }
  const registration = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
  await navigator.serviceWorker.ready;
  return registration;
}

/** 브라우저 종료 후 푸시 수신을 위해 SW를 먼저 등록·활성화 */
export async function warmUpPushServiceWorker() {
  if (!isPushEnvironmentReady() || !isFirebaseConfigured() || !hasBasicPushApis()) {
    return null;
  }
  try {
    return await ensureServiceWorker();
  } catch (e) {
    console.debug('FCM SW warm-up 실패:', e);
    return null;
  }
}

async function registerTokenIfLoggedIn(token) {
  if (!token || !hasAuthToken() || isImpersonationSession()) {
    return false;
  }
  await api.put('/api/users/me/fcm-token', { token, platform: 'web' });
  cachedToken = token;
  return true;
}

/**
 * @returns {'unsupported'|'granted'|'denied'|'default'}
 */
export function getPushPermissionState() {
  if (!isPushEnvironmentReady() || !isFirebaseConfigured() || !hasBasicPushApis()) {
    return 'unsupported';
  }
  return Notification.permission;
}

function attachForegroundListener(messaging) {
  if (foregroundListenerAttached) {
    return;
  }
  foregroundListenerAttached = true;
  onMessage(messaging, (payload) => {
    if (Notification.permission !== 'granted') {
      console.warn('[web-fcm] 포그라운드 수신 스킵: Notification.permission=', Notification.permission);
      return;
    }
    const n = payload.notification || {};
    const data = payload.data || {};
    const pushType = data.type || 'chat';
    console.info('[web-fcm] 포그라운드 수신', {
      pushType,
      eventType: data.eventType,
      title: data.title || n.title,
      body: data.body || n.body,
      targetScreen: data.targetScreen,
    });
    const title = resolvePushNotificationTitle({
      dataTitle: data.title,
      notificationTitle: n.title,
      pushType,
    });
    const body = n.body || data.body || '';
    const roomId = data.roomId;
    if (data.type === 'chat' && roomId && shouldSuppressChatNotification(roomId)) {
      return;
    }
    showChatBrowserNotification({
      title,
      body,
      roomId,
      messageId: data.messageId,
      pushType,
      resourceId: data.resourceId || data.designRequestId || data.subscriptionRequestId,
      eventType: data.eventType,
      targetScreen: data.targetScreen,
    });
  });
}

/**
 * @param {{ requestPermissionIfNeeded?: boolean }} options
 * @returns {Promise<'registered'|'permission_required'|'denied'|'unsupported'|'error'>}
 */
export async function registerForPushIfPossible(options = {}) {
  const { requestPermissionIfNeeded = false } = options;
  if (registerInFlight) {
    return registerInFlight;
  }

  registerInFlight = doRegisterForPushIfPossible(requestPermissionIfNeeded);
  try {
    return await registerInFlight;
  } finally {
    registerInFlight = null;
  }
}

async function doRegisterForPushIfPossible(requestPermissionIfNeeded) {
  try {
    if (!isPushEnvironmentReady() || !isFirebaseConfigured() || !hasBasicPushApis()) {
      return 'unsupported';
    }
    if (Notification.permission === 'denied') {
      return 'denied';
    }

    await warmUpPushServiceWorker();

    if (Notification.permission === 'default') {
      if (!requestPermissionIfNeeded) {
        return 'permission_required';
      }
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        return perm === 'denied' ? 'denied' : 'permission_required';
      }
    }

    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      return 'unsupported';
    }

    const swReg = await navigator.serviceWorker.getRegistration(SW_PATH);
    const token = await getToken(messaging, {
      vapidKey: getVapidKey(),
      serviceWorkerRegistration: swReg || (await ensureServiceWorker()),
    });
    if (!token) {
      console.warn('FCM getToken: 토큰 없음');
      return 'error';
    }

    await registerTokenIfLoggedIn(token);
    attachForegroundListener(messaging);
    return 'registered';
  } catch (e) {
    console.warn('FCM 웹 등록 실패:', e);
    return 'error';
  }
}

/** 사용자 클릭(제스처) 후 호출 — Edge 등에서 권한 창 표시 */
export async function requestPushPermissionAndRegister() {
  return registerForPushIfPossible({ requestPermissionIfNeeded: true });
}

export async function unregisterFromBackend() {
  try {
    if (!hasAuthToken() || isImpersonationSession()) {
      cachedToken = null;
      return;
    }
    const messaging = await getFirebaseMessaging();
    let token = cachedToken;
    if (!token && messaging) {
      const swReg = await navigator.serviceWorker.getRegistration(SW_PATH);
      token = await getToken(messaging, {
        vapidKey: getVapidKey(),
        serviceWorkerRegistration: swReg,
      });
    }
    if (!token) {
      return;
    }
    await api.delete('/api/users/me/fcm-token', { data: { token } });
    cachedToken = null;
  } catch (e) {
    console.debug('FCM 웹 해제 실패(무시):', e);
  }
}
